import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { useCountries } from '@/application/queries'
import { Country, toCountryCodes } from '@/domain/countries'
import { CountryReturns } from '@/domain/returns'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { useFilter } from '@/application/use-filter'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  countries?: string[],
  collections?: string[]
) {
  return [
    'countriesReturns',
    ecommerceIds,
    startDate,
    endDate,
    countries,
    collections
  ]
}

export function useCountriesReturns(startDate?: string, endDate?: string) {
  const { t } = useTranslation()
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  async function callCountriesReturns() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.getCountriesReturns({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections
    })
  }

  const countriesReturnsQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      selectedCountries,
      selectedCollections
    ),
    queryFn: callCountriesReturns,
    staleTime: 1000 * 60 * 15, // 15 minutes
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  const countriesReturns = toCountriesReturnsWithCountryName(
    countries,
    countriesReturnsQuery.data ?? [],
    t
  )

  const countryList = countriesReturns.map((entry) => entry.country)
  const countriesTotalReturns = countriesReturns.map((entry) =>
    entry.totalReturns.toString()
  )
  const countriesPercentage = countriesReturns.map((entry) => entry.percentage)
  const totalReturns = countriesReturns
    .reduce((acc, val) => acc + val.totalReturns, 0)
    .toString()

  return {
    countriesReturns,
    countryList,
    countriesTotalReturns,
    countriesPercentage,
    totalReturns,
    loadingCountriesReturns: countriesReturnsQuery.isLoading || loadingCountries
  }
}

function toCountriesReturnsWithCountryName(
  countries: Country[],
  countriesReturns: CountryReturns[],
  t: TFunction
) {
  return countriesReturns.map((entry) => {
    const country = countries.find((c) => c.iso === entry.country)
    const finalCountry =
      entry.country === 'OTHER'
        ? t('returns_analytics.others')
        : entry.country === 'NO_COUNTRY'
          ? t('returns_analytics.unknown')
          : country?.name
    return {
      ...entry,
      country: finalCountry ?? entry.country
    }
  })
}
